import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { toast } from 'sonner';
import { ArrowLeft, Award, Calendar as CalendarIcon, MapPin, Loader2, Heart, MessageCircle, Settings, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import ImageWithLoader from '@/components/ImageWithLoader';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${BACKEND_URL}${path}`;
};

const MedalIcon = ({ count }) => {
  if (count === 0) return null;
  return (
    <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-semibold">
      <Award size={12} />
      <span>{count}</span>
    </div>
  );
};

export default function UserProfile({ userId, auth, isOwnProfile = false }) {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Entry detail logic
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEntryDetail, setShowEntryDetail] = useState(false);
  const [loadingEntryDetail, setLoadingEntryDetail] = useState(false);
  const [likers, setLikers] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadUserProfile();
  }, [userId]);

  const loadUserProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const [userRes, statsRes, entriesRes] = await Promise.all([
        axios.get(`${API}/user/${userId}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        }),
        axios.get(`${API}/users/${userId}/stats`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        }),
        axios.get(`${API}/entries/user/${userId}`, {
          headers: { Authorization: `Bearer ${auth.token}` }
        })
      ]);

      setUser(userRes.data);
      setStats(statsRes.data);
      setEntries(entriesRes.data);
    } catch (err) {
      console.error("Failed to load user profile", err);
      setError("Failed to load user profile");
      toast.error("Failed to load user profile");
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = async (date) => {
      if (!date) return;
      const dateStr = format(date, 'yyyy-MM-dd');
      const entryIdx = entries.findIndex(e => e.date === dateStr);

      if (entryIdx === -1) {
          return;
      }

      const basicEntry = entries[entryIdx];
      if (!basicEntry.completed) return;

      setLoadingEntryDetail(true);
      try {
          const response = await axios.get(`${API}/entries/${basicEntry.id}`, {
              headers: { Authorization: `Bearer ${auth.token}` }
          });
          setSelectedEntry(response.data);
          setShowEntryDetail(true);

          // Fetch likes
          fetchLikers(basicEntry.id);
      } catch (err) {
          console.error("Failed to load entry details", err);
          toast.error("Failed to load entry details");
      } finally {
          setLoadingEntryDetail(false);
      }
  };

  const fetchLikers = async (entryId) => {
      try {
          const res = await axios.get(`${API}/likes/entry/${entryId}`, {
              headers: { Authorization: `Bearer ${auth.token}` }
          });
          setLikers(res.data.likes);
      } catch (err) {
          console.error("Failed to fetch likers", err);
      }
  };

  const handleLike = async () => {
      if (!selectedEntry) return;
      try {
          const response = await axios.post(`${API}/likes`, {
              entry_id: selectedEntry.id
          }, {
              headers: { Authorization: `Bearer ${auth.token}` }
          });

          const isLiked = response.data.liked;

          setSelectedEntry(prev => ({
              ...prev,
              is_liked: isLiked,
              likes_count: isLiked ? prev.likes_count + 1 : prev.likes_count - 1
          }));

          // Refresh likers list
          fetchLikers(selectedEntry.id);

      } catch (err) {
          toast.error("Failed to update like");
      }
  };

  const handleNavigate = async (direction) => {
    if (!selectedEntry) return;

    // Find current index
    const currentIndex = entries.findIndex(e => e.id === selectedEntry.id);
    if (currentIndex === -1) return;

    // Find next/prev completed entry
    let newIndex = currentIndex;
    let found = false;

    while (!found) {
        newIndex += direction;
        if (newIndex < 0 || newIndex >= entries.length) break;
        if (entries[newIndex].completed) {
            found = true;
        }
    }

    if (found) {
        const nextEntry = entries[newIndex];
        setLoadingEntryDetail(true);
        try {
            const response = await axios.get(`${API}/entries/${nextEntry.id}`, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            setSelectedEntry(response.data);
            fetchLikers(nextEntry.id);
        } catch (err) {
            console.error("Failed to navigate", err);
            toast.error("Failed to load entry");
        } finally {
            setLoadingEntryDetail(false);
        }
    }
  };

  const hasNextPost = () => {
    if (!selectedEntry) return false;
    const currentIndex = entries.findIndex(e => e.id === selectedEntry.id);
    if (currentIndex === -1) return false;

    for (let i = currentIndex + 1; i < entries.length; i++) {
        if (entries[i].completed) return true;
    }
    return false;
  };

  const hasPrevPost = () => {
    if (!selectedEntry) return false;
    const currentIndex = entries.findIndex(e => e.id === selectedEntry.id);
    if (currentIndex === -1) return false;

    for (let i = currentIndex - 1; i >= 0; i--) {
        if (entries[i].completed) return true;
    }
    return false;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showEntryDetail) return;
      if (e.key === 'ArrowLeft') {
        handleNavigate(-1);
      } else if (e.key === 'ArrowRight') {
        handleNavigate(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showEntryDetail, selectedEntry, entries]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-4 pb-24">
        <div className="max-w-2xl mx-auto space-y-4">
          {!isOwnProfile && (
             <Skeleton className="h-10 w-24 mb-2 rounded-md bg-white/50" />
          )}

          {/* Header Skeleton */}
          <Card className="p-6 glass relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-24 bg-gray-100/50" />
            <div className="relative flex flex-col sm:flex-row items-center gap-6">
                <Skeleton className="w-24 h-24 rounded-full border-4 border-white shadow-sm" />
                <div className="flex-1 flex flex-col items-center sm:items-start gap-3 w-full sm:w-auto">
                    <Skeleton className="h-8 w-48 bg-gray-200" />
                    <Skeleton className="h-4 w-32 bg-gray-200" />
                </div>
            </div>
          </Card>

          {/* Stats Skeleton */}
          <div className="grid grid-cols-2 gap-4">
              <Card className="p-4 glass h-24 flex flex-col items-center justify-center gap-2">
                  <Skeleton className="h-9 w-16 bg-gray-200" />
                  <Skeleton className="h-3 w-20 bg-gray-200" />
              </Card>
              <Card className="p-4 glass h-24 flex flex-col items-center justify-center gap-2">
                  <Skeleton className="h-9 w-16 bg-gray-200" />
                  <Skeleton className="h-3 w-20 bg-gray-200" />
              </Card>
          </div>

          {/* Monthly Progress Skeleton */}
           <Card className="p-6 glass">
               <Skeleton className="h-6 w-40 mb-4 bg-gray-200" />
               <div className="space-y-3">
                   {[1, 2, 3].map(i => (
                       <Skeleton key={i} className="h-16 w-full rounded-lg bg-gray-100" />
                   ))}
               </div>
           </Card>

           {/* Calendar Skeleton */}
           <Card className="p-6 glass">
               <div className="flex items-center justify-between mb-4">
                   <Skeleton className="h-6 w-40 bg-gray-200" />
                   <Skeleton className="h-3 w-48 bg-gray-200" />
               </div>
               <div className="flex justify-center py-4">
                   <Skeleton className="h-[300px] w-[300px] rounded-xl bg-gray-100" />
               </div>
           </Card>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-4 flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">User not found</h2>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-4 pb-24">
      <div className="max-w-2xl mx-auto space-y-3">
        {/* Navigation */}
        {!isOwnProfile && (
            <Button
            variant="ghost"
            className="text-gray-600 hover:text-gray-900 -ml-2 h-8"
            onClick={() => navigate(-1)}
            >
            <ArrowLeft className="mr-2" size={16} />
            Back
            </Button>
        )}

        {/* Header content */}
        <Card className="p-6 glass relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-r from-teal-500/10 to-cyan-500/10" />
            <div className="relative flex flex-col sm:flex-row items-center gap-6">
                <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                    {user.profile_picture ? (
                        <ImageWithLoader
                            src={getImageUrl(user.profile_picture)}
                            alt={user.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <AvatarFallback className="text-3xl bg-teal-100 text-teal-700">
                            {user.name[0]}{user.surname[0]}
                        </AvatarFallback>
                    )}
                </Avatar>

                <div className="text-center sm:text-left flex-1">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center justify-center sm:justify-start gap-2">
                        {user.name} {user.surname}
                        {stats?.medals_count > 0 && <MedalIcon count={stats.medals_count} />}
                    </h1>

                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-2 text-sm text-gray-600 justify-center sm:justify-start">
                        {user.location && (
                            <div className="flex items-center gap-1">
                                <MapPin size={14} />
                                <span>{user.location}</span>
                            </div>
                        )}
                    </div>
                </div>

                {isOwnProfile && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/profile/account')}
                        className="flex items-center gap-2 mt-4 sm:mt-0"
                    >
                        <Settings size={16} />
                        Account Center
                    </Button>
                )}
            </div>
        </Card>

        {/* Stats */}
        {stats && (
            <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 glass text-center">
                    <div className="text-3xl font-bold text-teal-600">{stats.completion_percentage}%</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mt-1">Completion</div>
                </Card>
                <Card className="p-4 glass text-center">
                    <div className="text-3xl font-bold text-amber-500 flex items-center justify-center gap-1">
                         <Award size={24} />
                         {stats.medals_count}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mt-1">Medals</div>
                </Card>
            </div>
        )}

        {/* Medals Display */}
        {stats && stats.successful_months.length > 0 && (
            <Card className="p-6 glass">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <Award className="text-amber-500" size={20} />
                    Medals Earned
                </h3>
                <div className="flex flex-wrap gap-2">
                    {stats.successful_months.map((month) => (
                        <div key={month.month} className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 shadow-sm border border-amber-200">
                            <Award size={14} />
                            <span>{month.challenge_name}</span>
                            <span className="text-amber-600 text-xs">({month.completion_rate}%)</span>
                        </div>
                    ))}
                </div>
            </Card>
        )}

        {/* Monthly Progress List */}
        {stats && stats.monthly_progress && stats.monthly_progress.length > 0 && (
            <Card className="p-6 glass">
                 <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <CalendarIcon className="text-teal-600" size={20} />
                    Monthly Progress
                </h3>
                <div className="space-y-3">
                    {stats.monthly_progress.map((progress) => (
                        <div key={progress.month} className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-gray-100">
                             <div className="flex flex-col">
                                <span className="font-medium text-gray-800">
                                    {format(new Date(progress.month + '-01'), 'MMMM')}
                                </span>
                                <span className="text-xs text-gray-500">{progress.challenge_name}</span>
                             </div>
                             <div className="text-right">
                                <div className="text-sm font-bold text-teal-600">{progress.completion_rate}%</div>
                                <div className="text-xs text-gray-500">{progress.completed_days}/{progress.total_days} days</div>
                             </div>
                        </div>
                    ))}
                </div>
            </Card>
        )}

        {/* Calendar */}
        <Card className="p-6 glass">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <CalendarIcon className="text-teal-600" size={20} />
                    Challenge Calendar
                </h3>
                <span className="text-xs text-gray-500 italic">Click on a day to retrieve activity</span>
            </div>

            <div className="flex justify-center">
                 <Calendar
                    mode="single"
                    modifiers={{
                      completed: entries
                        .filter(e => e.completed)
                        .map(e => new Date(e.date)),
                      incomplete: entries
                        .filter(e => !e.completed)
                        .map(e => new Date(e.date))
                    }}
                    modifiersClassNames={{
                      completed: 'calendar-day-completed',
                      incomplete: 'calendar-day-incomplete'
                    }}
                    onSelect={handleDateClick}
                    className="p-0"
                  />
            </div>

             <div className="flex flex-wrap gap-4 text-xs mt-6 justify-center text-gray-500">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span>Completed</span>
                </div>
              </div>
        </Card>
      </div>

      {/* Entry Detail Modal */}
      <Dialog open={showEntryDetail} onOpenChange={setShowEntryDetail}>
        <DialogContent className="w-[95vw] h-[90vh] sm:max-w-xl sm:h-[85vh] p-0 flex flex-col gap-0 overflow-hidden">
          {selectedEntry ? (
            <>
              <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
                  <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleNavigate(-1)}
                      disabled={!hasPrevPost() || loadingEntryDetail}
                      className="rounded-full bg-white/80 shadow-sm hover:bg-white"
                  >
                      <ChevronLeft size={24} />
                  </Button>
              </div>

              <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
                  <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleNavigate(1)}
                      disabled={!hasNextPost() || loadingEntryDetail}
                      className="rounded-full bg-white/80 shadow-sm hover:bg-white"
                  >
                      <ChevronRight size={24} />
                  </Button>
              </div>

              <DialogHeader className="p-6 pb-2 flex-shrink-0">
                <DialogTitle>
                  {format(new Date(selectedEntry.date), 'MMMM d, yyyy')}
                </DialogTitle>
              </DialogHeader>


              <div className="flex-1 overflow-y-auto p-6 pt-2 space-y-4">
                {selectedEntry.image && (
                  <div className="bg-black/5 rounded-lg overflow-hidden flex items-center justify-center h-[50vh] min-h-[300px] flex-shrink-0">
                     <ImageWithLoader
                        src={getImageUrl(selectedEntry.image)}
                        alt="Entry proof"
                        className="w-full h-full object-contain"
                      />
                  </div>
                )}

                <div className="flex flex-col gap-2 mt-2 px-2">
                    <div className="flex items-center gap-4 text-gray-600">
                        <button
                            className={`flex items-center gap-1 transition-colors ${selectedEntry.is_liked ? "text-red-500" : "hover:text-red-500"}`}
                            onClick={handleLike}
                        >
                            <Heart size={20} className={selectedEntry.is_liked ? "fill-red-500" : ""} />
                            <span className="text-sm font-medium">{selectedEntry.likes_count}</span>
                        </button>
                        <div className="flex items-center gap-1">
                            <MessageCircle size={20} />
                            <span className="text-sm font-medium">{selectedEntry.comments?.length || 0}</span>
                        </div>
                    </div>

                    {likers.length > 0 && (
                        <div className="text-xs text-gray-500">
                            Liked by {likers.map(l => l.name).slice(0, 3).join(', ')}
                            {likers.length > 3 && ` and ${likers.length - 3} others`}
                        </div>
                    )}
                </div>

                {selectedEntry.comment && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700">{selectedEntry.comment}</p>
                  </div>
                )}

                {selectedEntry.comments && selectedEntry.comments.length > 0 && (
                    <div className="mt-4 space-y-3">
                        <h4 className="font-semibold text-sm text-gray-700">Recent Comments</h4>
                        <div className="space-y-3 max-h-40 overflow-y-auto pr-2">
                            {selectedEntry.comments.map((comment) => (
                                <div key={comment.id} className="bg-white border rounded-lg p-3 text-sm shadow-sm">
                                    <div className="flex justify-between items-start mb-1">
                                        <span className="font-semibold text-gray-900">{comment.user_name}</span>
                                        <span className="text-xs text-gray-500">{format(new Date(comment.created_at), 'MMM d')}</span>
                                    </div>
                                    <p className="text-gray-700">{comment.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!selectedEntry.image && !selectedEntry.comment && (!selectedEntry.comments || selectedEntry.comments.length === 0) && (
                    <p className="text-center text-gray-500 py-4">Challenge completed!</p>
                )}
              </div>
            </>
          ) : (
             <div className="flex items-center justify-center py-10">
                 <Loader2 className="animate-spin text-teal-600" />
             </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
